import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import { getUtmParams, clearUtm } from '../../utils/utm';
import { getFingerprint } from '../../utils/fingerprint';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (token) {
        try {
          await loginWithToken(token);

          // Save fingerprint against the now-authenticated user
          getFingerprint().then(fp => {
            apiClient.post('/api/v1/auth/fingerprint', { fingerprint: fp }).catch(() => {});
          });

          // Check for stored redirect, then default based on user status
          const isNewUser = searchParams.get('is_new') === 'true';
          const storedRedirect = sessionStorage.getItem('oauth_redirect');
          sessionStorage.removeItem('oauth_redirect');
          sessionStorage.removeItem('auth-return-url');

          // Save UTM params for new Google users then clear them
          if (isNewUser) {
            const utmParams = getUtmParams();
            if (Object.keys(utmParams).length) {
              apiClient.post('/api/v1/auth/utm', utmParams).catch(() => {});
              clearUtm();
            }
          }

          // Create pre-paid Etsy invite for new Google users who landed via Etsy link
          if (isNewUser) {
            const pendingEtsy = localStorage.getItem('etsy_pending');
            if (pendingEtsy) {
              localStorage.removeItem('etsy_pending');
              try {
                const { theme } = JSON.parse(pendingEtsy);
                if (theme) {
                  const claimRes = await apiClient.post('/api/v1/invites/etsy-claim', { theme });
                  const inviteId = claimRes.data?.invite_id;
                  if (inviteId) {
                    navigate(`/app/invites/${inviteId}/edit?welcome=etsy`, { replace: true });
                    return;
                  }
                }
              } catch { /* fall through to normal redirect */ }
            }
          }

          const redirect = storedRedirect || (isNewUser ? '/onboarding' : '/app/home');
          navigate(redirect, { replace: true });
        } catch {
          setError('Failed to load user data. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
        }
      } else {
        setError('No authentication token received.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, loginWithToken]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-error mb-2">{error}</p>
          <p className="text-text-secondary text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-text-secondary">Completing sign in...</p>
      </div>
    </div>
  );
}
